"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { getLink, Routes } from "@/constants";
import { Translations } from "@/translations";
import QuizImg from "../../../public/assets/lm-images/quiz.jpg";
import StudentsImg from "../../../public/assets/lm-images/students2.jpg";

const LEARNING_TOOLS_PROMO_VIDEO_URL =
  "https://pub-93afee7b00104ad6a5e2e7390b938f73.r2.dev/promo/every-teaching-tool-in-one-window.mp4";

const actionStyle: React.CSSProperties = {
  whiteSpace: "normal",
  height: "auto",
  minHeight: "44px",
  padding: "12px 24px",
  lineHeight: 1.4,
  textAlign: "center",
};

const LearningToolsOverview: React.FC<{ locale: string }> = ({ locale }) => {
  const t = useTranslations("Homepage");
  const searchParams = useSearchParams();
  const sourceParam = searchParams.get("source")?.trim();
  const testerRequestBaseUrl = getLink(locale, Routes.CLASSROOM_TESTER_REQUEST);
  const testerRequestUrl = sourceParam
    ? `${testerRequestBaseUrl}?source=${encodeURIComponent(sourceParam)}`
    : testerRequestBaseUrl;
  const trialSignupParams = new URLSearchParams({
    testerIntent: "1",
    callbackUrl: testerRequestUrl,
  });
  const trialSignupUrl = `${getLink(locale, Routes.SIGN_UP)}?${trialSignupParams.toString()}`;
  const overviewLink = (route: string) => {
    const href = getLink(locale, route);
    return sourceParam ? `${href}?${new URLSearchParams({ source: sourceParam })}` : href;
  };

  const learningFlowItems = [
    {
      title: t(Translations.Homepage.LearningFlowClassroomsTitle),
      description: t(Translations.Homepage.LearningFlowClassroomsDescription),
      points: [
        t(Translations.Homepage.LearningFlowClassroomsPoint1),
        t(Translations.Homepage.LearningFlowClassroomsPoint2),
        t(Translations.Homepage.LearningFlowClassroomsPoint3),
      ],
      href: overviewLink(Routes.LEARNING_TOOLS),
      profileClassrooms: [
        { label: t(Translations.Homepage.LearningFlowProfileGeneral) },
        { label: t(Translations.Homepage.LearningFlowProfileMaths) },
        { label: t(Translations.Homepage.LearningFlowProfileLanguage) },
        { label: t(Translations.Homepage.LearningFlowProfileChess), href: getLink(locale, Routes.CHESS_FEATURES) },
      ],
    },
    {
      title: t(Translations.Homepage.LearningFlowQuizzesTitle),
      description: t(Translations.Homepage.LearningFlowQuizzesDescription),
      points: [
        t(Translations.Homepage.LearningFlowQuizzesPoint1),
        t(Translations.Homepage.LearningFlowQuizzesPoint2),
        t(Translations.Homepage.LearningFlowQuizzesPoint3),
      ],
      href: overviewLink(Routes.LEARNING_TOOLS_QUIZZES),
      profileClassrooms: [],
    },
  ];

  return (
    <div className="container">
      <div className="hero__content-wrapper mt-90" style={{ paddingBottom: "48px" }}>
        <div className="row align-items-center g-4">
          <div className="col-md-6">
            <div className="hero__content p-relative z-index-1">
              <h1 className="hero__title">
                <span>{t(Translations.Homepage.LearningToolsHeroTitleStart)}</span>
                {t(Translations.Homepage.LearningToolsHeroTitleEnd)}
              </h1>
              <p style={{ marginTop: "10px", marginBottom: "18px" }}>
                {t(Translations.Homepage.LearningToolsHeroDescriptionPrimary)}
              </p>
              <p>{t(Translations.Homepage.LearningToolsHeroDescriptionSecondary)}</p>
              <Link href={trialSignupUrl} className="e-btn e-btn-theme" style={actionStyle}>
                {t(Translations.Homepage.LearningFlowTestNowCta)}
              </Link>
            </div>
          </div>
          <div className="col-md-6">
            <div className="hero__thumb d-flex p-relative">
              <div className="hero__thumb-big mr-30">
                <Image src={QuizImg} style={{ width: "100%", height: "auto" }} alt={t(Translations.Homepage.LearningFlowQuizzesTitle)} />
              </div>
              <div className="hero__thumb-sm mt-50 d-none d-lg-block">
                <Image src={StudentsImg} unoptimized style={{ width: "100%", height: "auto" }} alt={t(Translations.Homepage.AudienceStudentsTitle)} />
              </div>
            </div>
          </div>
        </div>

        <section aria-label={t(Translations.Homepage.VideoTitle)} style={{ marginTop: "32px", marginBottom: "56px" }}>
          <video
            controls
            preload="metadata"
            poster="/assets/img/memo-dashboard.png"
            playsInline
            style={{ width: "100%", aspectRatio: "16 / 9", display: "block", borderRadius: "8px" }}
          >
            <source src={LEARNING_TOOLS_PROMO_VIDEO_URL} type="video/mp4" />
            <a href={LEARNING_TOOLS_PROMO_VIDEO_URL}>{t(Translations.Homepage.VideoTitle)}</a>
          </video>
        </section>

        <section aria-labelledby="learning-flow-title" style={{ marginBottom: "48px" }}>
          <div className="row g-4 align-items-start">
            <div className="col-lg-4">
              <h2 id="learning-flow-title" style={{ color: "#173b63", fontSize: "28px", marginBottom: "12px" }}>
                {t(Translations.Homepage.LearningFlowTitle)}
              </h2>
              <p style={{ color: "#5f7180" }}>{t(Translations.Homepage.LearningFlowDescription)}</p>
              <Link href={trialSignupUrl} className="e-btn e-btn-theme" style={actionStyle}>
                {t(Translations.Homepage.LearningFlowTestNowCta)}
              </Link>
            </div>
            <div className="col-lg-8">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "16px" }}>
                {learningFlowItems.map((item) => (
                  <article key={item.title} style={{ background: "#f8fbff", borderRadius: "8px", padding: "22px", border: "1px solid #e5eef8", minWidth: 0 }}>
                    <h3 style={{ fontSize: "20px", marginBottom: "8px", color: "#173b63" }}>{item.title}</h3>
                    <p style={{ margin: "0 0 10px 0", color: "#5f7180" }}>{item.description}</p>
                    <ul style={{ margin: "0 0 14px 0", paddingLeft: "18px", color: "#5f7180" }}>
                      {item.points.map((point) => <li key={point} style={{ marginBottom: "6px" }}>{point}</li>)}
                    </ul>
                    {item.profileClassrooms.length > 0 && (
                      <div style={{ marginBottom: "16px" }}>
                        <p style={{ marginBottom: "8px", color: "#4f6579", fontWeight: 600 }}>
                          {t(Translations.Homepage.LearningFlowProfileClassroomsLabel)}
                        </p>
                        <ul className="list-unstyled d-flex flex-wrap gap-3" style={{ marginBottom: 0 }}>
                          {item.profileClassrooms.map((profile) => (
                            <li key={profile.label}>
                              {profile.href ? <Link href={profile.href}>{profile.label}</Link> : profile.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Link href={item.href} className="e-btn e-btn-border" style={actionStyle}>
                      {t(Translations.Homepage.LearningFlowOpenCta)}
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LearningToolsOverview;